import type { IPromoterRepository } from "@/application/ports/repositories";
import type {
	PromoterDetail,
	PromoterDetailResponse,
	FiscalQuota,
	CreateFiscalQuotaRequest,
	UpdateFiscalQuotaRequest,
	PromoterMetrics,
	PromoterUpdateRequest,
	CreatePromoterResponse,
	PromoterListParams,
	PromoterMetricsParams,
} from "@/domain";
import type { PaginatedPromoters } from "@/domain/promoter";
import type {
	DocumentDetailsResponse,
	DocumentDownloadResponse,
	DocumentListResponse,
	ListDocumentsParams,
} from "@/domain/document";
import { DocumentType, EntityType } from "@/domain/documentEnums";
import { downloadBlob } from "../fileDownload";
import { apiClient, ApiClientError } from "../client";
import { getLogger } from "@/infrastructure/di/services";

export class PromoterRepository implements IPromoterRepository {
	private basePath = "/promoters";
	// --- Document helper methods (inlined from documentService) ---

	private async uploadBankCertificateDocument(
		promoterUuid: string,
		bankAccountUuid: string,
		certificate: File,
	): Promise<DocumentDetailsResponse> {
		const formData = new FormData();
		formData.append("document", certificate);
		formData.append("document_type", DocumentType.BANK_ACCOUNT_CERTIFICATE);
		formData.append("entity_type", EntityType.PROMOTER_BANK_ACCOUNT);
		formData.append("entity_uuid", bankAccountUuid);
		formData.append("title", "Bank Account Certificate");

		const response = await apiClient.upload<DocumentDetailsResponse>(
			`/promoters/${promoterUuid}/documents`,
			formData,
		);
		return response.data;
	}

	private async findBankCertificate(
		promoterUuid: string,
		bankAccountUuid: string,
	): Promise<DocumentDetailsResponse | null> {
		const queryParams = new URLSearchParams();
		queryParams.append("entity_type", EntityType.PROMOTER_BANK_ACCOUNT);
		queryParams.append("entity_uuid", bankAccountUuid);
		queryParams.append("document_type", DocumentType.BANK_ACCOUNT_CERTIFICATE);
		queryParams.append("limit", "1");

		const response = await apiClient.get<DocumentListResponse>(
			`/promoters/${promoterUuid}/documents?${queryParams.toString()}`,
		);
		return response.data.documents.length > 0
			? response.data.documents[0]
			: null;
	}

	// --- Promoter creation ---

	async createPromoter(
		data: Parameters<IPromoterRepository["createPromoter"]>[0],
	): Promise<CreatePromoterResponse> {
		const logger = getLogger();
		try {
			const basicPromoterData = {
				name: data.name,
				tax_id: data.tax_id,
				company_type: data.company_type,
				main_activities: data.main_activities,
				constitution_date: data.constitution_date || null,
				address: data.address || null,
				city: data.city || null,
				state: data.state || null,
				postal_code: data.postal_code || null,
				country: data.country || null,
				phone: data.phone || null,
				email: data.email || null,
				website: data.website || null,
				business_purpose: data.business_purpose || null,
				cnae_code: data.cnae_code || null,
				last_year_revenue: data.last_year_revenue || null,
				employee_count: data.employee_count ?? null,
				incentive_type: data.incentive_type || null,
			};

			const response = await apiClient.post<CreatePromoterResponse>(
				this.basePath,
				basicPromoterData,
				{ headers: { "Content-Type": "application/json" } },
			);

			const promoterId = response.data.uuid;

			if (data.legal_representative) {
				await apiClient.post(
					`/promoters/${promoterId}/legal-representatives`,
					data.legal_representative,
					{ headers: { "Content-Type": "application/json" } },
				);
			}

			if (data.banking_info) {
				try {
					const { certificate, ...bankingData } = data.banking_info;
					const bankAccountResponse = await apiClient.post<{ uuid: string }>(
						`/promoters/${promoterId}/bank-accounts`,
						bankingData,
						{ headers: { "Content-Type": "application/json" } },
					);
					if (certificate) {
						await this.uploadBankCertificateDocument(
							promoterId,
							bankAccountResponse.data.uuid,
							certificate,
						);
					}
				} catch (error) {
					logger.error("Error adding banking info:", error);
					throw error;
				}
			}

			return response.data;
		} catch (error) {
			logger.error("Error in sequential promoter creation:", error);
			throw error;
		}
	}

	// --- Metrics ---

	async getPromoterMetrics(
		params?: PromoterMetricsParams,
	): Promise<PromoterMetrics> {
		const queryParams = new URLSearchParams();
		if (params) {
			if (params.search) queryParams.append("search", params.search);
			if (params.status) queryParams.append("status", params.status);
			if (params.sector) queryParams.append("sector", params.sector);
		}

		const url = queryParams.toString()
			? `${this.basePath}/metrics?${queryParams.toString()}`
			: `${this.basePath}/metrics`;

		const response = await apiClient.get<PromoterMetrics>(url, {
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		});
		return response.data;
	}

	// --- CRUD ---

	async getPromoters(params?: PromoterListParams): Promise<{
		promoters: PromoterDetail[];
		total: number;
		page: number;
		total_pages: number;
	}> {
		const queryParams = new URLSearchParams();
		if (params) {
			if (params.search) queryParams.append("search", params.search);
			if (params.status) queryParams.append("status", params.status);
			if (params.sector) queryParams.append("sector", params.sector);
			if (params.sort_by) queryParams.append("sort_by", params.sort_by);
			if (params.sort_direction)
				queryParams.append("sort_direction", params.sort_direction);
			if (params.limit !== undefined)
				queryParams.append("limit", params.limit.toString());
			if (params.offset !== undefined)
				queryParams.append("offset", params.offset.toString());
		}

		const url = queryParams.toString()
			? `${this.basePath}?${queryParams.toString()}`
			: this.basePath;

		const response = await apiClient.get<PaginatedPromoters>(url, {
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		});

		const limit = params?.limit || 100;
		const offset = params?.offset || 0;
		const currentPage = Math.floor(offset / limit) + 1;
		const totalPages = Math.ceil((response.data.total_count || 0) / limit);

		return {
			promoters: response.data.promoters,
			total: response.data.total_count || response.data.promoters.length,
			page: currentPage,
			total_pages: totalPages,
		};
	}

	async getPromoter(id: string): Promise<PromoterDetailResponse> {
		const response = await apiClient.get<PromoterDetailResponse>(
			`${this.basePath}/${id}`,
		);
		return response.data;
	}

	async updatePromoter(
		id: string,
		data: PromoterUpdateRequest,
	): Promise<CreatePromoterResponse> {
		const response = await apiClient.patch<CreatePromoterResponse>(
			`${this.basePath}/${id}`,
			data,
		);
		return response.data;
	}

	async deletePromoter(id: string): Promise<void> {
		await apiClient.delete(`${this.basePath}/${id}`);
	}

	async archivePromoter(id: string): Promise<CreatePromoterResponse> {
		const response = await apiClient.patch<CreatePromoterResponse>(
			`${this.basePath}/${id}`,
			{
				status: "inactive",
			},
		);
		return response.data;
	}

	async unarchivePromoter(id: string): Promise<CreatePromoterResponse> {
		const response = await apiClient.patch<CreatePromoterResponse>(
			`${this.basePath}/${id}`,
			{
				status: "active",
			},
		);
		return response.data;
	}

	// --- Fiscal Quotas ---

	async getFiscalQuotas(promoterUuid: string): Promise<FiscalQuota[]> {
		const response = await apiClient.get<FiscalQuota[]>(
			`/promoters/${promoterUuid}/fiscal-quotas`,
		);
		return response.data;
	}

	async createFiscalQuota(
		promoterUuid: string,
		data: CreateFiscalQuotaRequest,
	): Promise<FiscalQuota> {
		const response = await apiClient.post<FiscalQuota>(
			`/promoters/${promoterUuid}/fiscal-quotas`,
			data,
			{ headers: { "Content-Type": "application/json" } },
		);
		return response.data;
	}

	async updateFiscalQuota(
		promoterUuid: string,
		quotaUuid: string,
		data: UpdateFiscalQuotaRequest,
	): Promise<FiscalQuota> {
		const response = await apiClient.patch<FiscalQuota>(
			`/promoters/${promoterUuid}/fiscal-quotas/${quotaUuid}`,
			data,
			{ headers: { "Content-Type": "application/json" } },
		);
		return response.data;
	}

	async deleteFiscalQuota(
		promoterUuid: string,
		quotaUuid: string,
	): Promise<void> {
		await apiClient.delete(
			`/promoters/${promoterUuid}/fiscal-quotas/${quotaUuid}`,
		);
	}

	// --- Bank Certificates ---

	async uploadBankCertificate(
		promoterUuid: string,
		accountUuid: string,
		certificate: File,
	): Promise<DocumentDetailsResponse> {
		return this.uploadBankCertificateDocument(
			promoterUuid,
			accountUuid,
			certificate,
		);
	}

	async getBankCertificate(
		promoterUuid: string,
		accountUuid: string,
	): Promise<DocumentDownloadResponse> {
		const certificate = await this.findBankCertificate(
			promoterUuid,
			accountUuid,
		);
		if (!certificate) {
			throw new ApiClientError({
				message: "No certificate found for this bank account",
				code: "NOT_FOUND",
				status: 404,
			});
		}
		const response = await apiClient.get<DocumentDownloadResponse>(
			`/promoters/${promoterUuid}/documents/${certificate.uuid}/download`,
		);
		return response.data;
	}

	async deleteBankCertificate(
		promoterUuid: string,
		accountUuid: string,
	): Promise<void> {
		const certificate = await this.findBankCertificate(
			promoterUuid,
			accountUuid,
		);
		if (certificate) {
			await apiClient.delete(
				`/promoters/${promoterUuid}/documents/${certificate.uuid}`,
			);
		}
	}

	async getBankCertificateDetails(
		promoterUuid: string,
		accountUuid: string,
	): Promise<DocumentDetailsResponse | null> {
		return this.findBankCertificate(promoterUuid, accountUuid);
	}

	// --- Legal Representatives ---

	async updateLegalRepresentative(
		promoterUuid: string,
		representativeUuid: string,
		data: {
			full_name?: string;
			position?: string;
			dni?: string;
			phone?: string;
			email?: string;
			is_primary?: boolean;
		},
	): Promise<void> {
		await apiClient.patch(
			`/promoters/${promoterUuid}/legal-representatives/${representativeUuid}`,
			data,
			{ headers: { "Content-Type": "application/json" } },
		);
	}

	// --- Bank Accounts ---

	async updateBankAccount(
		promoterUuid: string,
		accountUuid: string,
		data: {
			bank_name?: string;
			iban?: string;
			swift?: string;
			is_primary?: boolean;
		},
	): Promise<void> {
		await apiClient.patch(
			`/promoters/${promoterUuid}/bank-accounts/${accountUuid}`,
			data,
			{
				headers: { "Content-Type": "application/json" },
			},
		);
	}

	// --- CSV Export ---

	async exportToCsv(
		params?: Omit<PromoterListParams, "limit" | "offset">,
	): Promise<void> {
		const queryParams = new URLSearchParams();
		if (params) {
			if (params.search) queryParams.append("search", params.search);
			if (params.status) queryParams.append("status", params.status);
			if (params.sector) queryParams.append("sector", params.sector);
			if (params.sort_by) queryParams.append("sort_by", params.sort_by);
			if (params.sort_direction)
				queryParams.append("sort_direction", params.sort_direction);
		}

		const url = queryParams.toString()
			? `${this.basePath}/export?${queryParams.toString()}`
			: `${this.basePath}/export`;

		const { blob, filename } = await apiClient.getBlobWithFilename(
			url,
			"promoters.csv",
			{
				headers: { Accept: "text/csv" },
			},
		);

		downloadBlob(blob, filename);
	}

	// --- Document operations ---

	async uploadDocument(
		promoterUuid: string,
		file: File,
		documentType: DocumentType,
		entityType: EntityType,
		entityUuid: string,
		title?: string,
	): Promise<DocumentDetailsResponse> {
		const formData = new FormData();
		formData.append("document", file);
		formData.append("document_type", documentType);
		formData.append("entity_type", entityType);
		formData.append("entity_uuid", entityUuid);
		if (title) {
			formData.append("title", title);
		}
		const response = await apiClient.upload<DocumentDetailsResponse>(
			`/promoters/${promoterUuid}/documents`,
			formData,
		);
		return response.data;
	}

	async listDocuments(
		promoterUuid: string,
		params?: ListDocumentsParams,
	): Promise<DocumentListResponse> {
		const queryParams = new URLSearchParams();
		if (params) {
			if (params.entity_type)
				queryParams.append("entity_type", params.entity_type);
			if (params.entity_uuid)
				queryParams.append("entity_uuid", params.entity_uuid);
			if (params.document_type)
				queryParams.append("document_type", params.document_type);
			if (params.status) queryParams.append("status", params.status);
			if (params.search) queryParams.append("search", params.search);
			if (params.limit) queryParams.append("limit", params.limit.toString());
			if (params.offset) queryParams.append("offset", params.offset.toString());
		}
		const url = queryParams.toString()
			? `/promoters/${promoterUuid}/documents?${queryParams.toString()}`
			: `/promoters/${promoterUuid}/documents`;
		const response = await apiClient.get<DocumentListResponse>(url);
		return response.data;
	}

	async getDocument(
		promoterUuid: string,
		documentUuid: string,
	): Promise<DocumentDetailsResponse> {
		const response = await apiClient.get<DocumentDetailsResponse>(
			`/promoters/${promoterUuid}/documents/${documentUuid}`,
		);
		return response.data;
	}

	async getDocumentDownloadUrl(
		promoterUuid: string,
		documentUuid: string,
	): Promise<DocumentDownloadResponse> {
		const response = await apiClient.get<DocumentDownloadResponse>(
			`/promoters/${promoterUuid}/documents/${documentUuid}/download`,
		);
		return response.data;
	}

	async deleteDocument(
		promoterUuid: string,
		documentUuid: string,
	): Promise<void> {
		await apiClient.delete(
			`/promoters/${promoterUuid}/documents/${documentUuid}`,
		);
	}

	async approveDocument(
		promoterUuid: string,
		documentUuid: string,
		notes?: string,
	): Promise<DocumentDetailsResponse> {
		const response = await apiClient.patch<DocumentDetailsResponse>(
			`/promoters/${promoterUuid}/documents/${documentUuid}/approve`,
			notes ? { notes } : undefined,
		);
		return response.data;
	}

	async rejectDocument(
		promoterUuid: string,
		documentUuid: string,
		notes?: string,
	): Promise<DocumentDetailsResponse> {
		const response = await apiClient.patch<DocumentDetailsResponse>(
			`/promoters/${promoterUuid}/documents/${documentUuid}/reject`,
			notes ? { notes } : undefined,
		);
		return response.data;
	}
}
